import { useQuery } from "@tanstack/react-query";
import React from "react";
import { getDashboardOverviewMetrics } from "../../../Actions/DashboardActions";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PerformanceTrends = ({ user }) => {
  const { data: metricsData, isLoading } = useQuery({
    queryFn: () => getDashboardOverviewMetrics(user?.organizationId),
    queryKey: ["dashboardOverviewMetrics", user?.organizationId],
    enabled: !!user?.organizationId,
    staleTime: 30000,
    cacheTime: 60000,
  });

  const trends = metricsData?.trends || {};
  const monthlyData = trends.monthly || [];

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Generate last 6 months labels if no data
  const generateLast6Months = () => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${monthNames[d.getMonth()]} ${d.getFullYear()}`);
    }
    return months;
  };

  const labels = monthlyData.length > 0 
    ? monthlyData.map((d) => `${monthNames[d._id.month - 1]} ${d._id.year}`)
    : generateLast6Months();

  // Google-like bar chart styling
  const barChartData = {
    labels: labels,
    datasets: [
      {
        label: "Completes",
        data: monthlyData.length > 0 ? monthlyData.map((d) => d.completes) : [0, 0, 0, 0, 0, 0],
        backgroundColor: "#4285F4",
        hoverBackgroundColor: "#1a73e8",
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 24,
      },
      {
        label: "Hits",
        data: monthlyData.length > 0 ? monthlyData.map((d) => d.hits) : [0, 0, 0, 0, 0, 0],
        backgroundColor: "#FBBC04",
        hoverBackgroundColor: "#f9a825",
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 24,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
          boxWidth: 12,
          boxHeight: 12,
          padding: 20,
          font: { 
            size: 12,
            family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
            weight: "500",
          },
          color: "#5f6368",
        },
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#202124",
        bodyColor: "#5f6368",
        borderColor: "#dadce0",
        borderWidth: 1,
        padding: 12,
        titleFont: { 
          size: 13,
          family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
          weight: "500",
        },
        bodyFont: { 
          size: 12,
          family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
        },
        cornerRadius: 8,
        boxPadding: 4,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            return ` ${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: { 
          display: false,
        },
        ticks: { 
          font: { 
            size: 11,
            family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
          }, 
          color: "#80868b",
          padding: 8,
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: { 
          color: "#f1f3f4",
          drawBorder: false,
        },
        ticks: { 
          font: { 
            size: 11,
            family: "'Google Sans', 'Roboto', 'Arial', sans-serif",
          }, 
          color: "#80868b",
          padding: 12,
          callback: function(value) {
            return value.toLocaleString();
          }
        },
        border: {
          display: false,
        },
        beginAtZero: true,
      },
    },
  };

  // Calculate totals
  const totalCompletes = monthlyData.reduce((acc, d) => acc + d.completes, 0);
  const totalHits = monthlyData.reduce((acc, d) => acc + d.hits, 0);

  if (isLoading) {
    return (
      <div className="bg-white border border-[#E0E0E0] rounded-xl p-6 h-[340px] animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
        <div className="h-4 bg-gray-100 rounded w-64 mb-6"></div>
        <div className="h-full bg-gray-50 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#dadce0] rounded-xl overflow-hidden shadow-sm">
      <div className="px-6 py-5 border-b border-[#E0E0E0] flex items-center justify-between">
        <div>
          <h3 className="text-[#202124] text-[16px] font-[500]">
            Performance Trend
          </h3>
          <p className="text-[13px] text-[#5f6368] mt-1">
            Completes & hits over the last 6 months
          </p>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[#4285F4]"></div>
            <span className="text-[13px] text-[#5f6368]">
              <span className="font-semibold text-[#202124]">{totalCompletes.toLocaleString()}</span> Completes
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[#FBBC04]"></div>
            <span className="text-[13px] text-[#5f6368]">
              <span className="font-semibold text-[#202124]">{totalHits.toLocaleString()}</span> Hits
            </span>
          </div>
        </div>
      </div>
      <div className="p-6 h-[280px]">
        <Bar data={barChartData} options={barChartOptions} />
      </div>
    </div>
  );
};

export default PerformanceTrends;
